import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { LockClosedIcon } from "@radix-ui/react-icons";
import type {
  AmplitudeClientLike,
  IrisEnvironment,
  LaunchDarklyClientLike,
} from "./irisCtaRuntime";
import {
  getInitialIMessageUrl,
  isIrisCtaWaitlistEnabled,
  isIOS,
  resolveIMessageUrl,
  trackIrisCtaClickWithOptions,
} from "./irisCtaRuntime";
import { PillLink } from "./PillLink";
import { useAmplitude } from "../lib/AmplitudeProvider";
import { useWaitlistModal } from "./waitlistModalContext";
import { isCurrentUserOffWaitlist } from "../lib/waitlistUser";

type IrisCtaLinkProps = {
  // Same value as Amplitude's device id.
  deviceId?: string;
  ldClient?: LaunchDarklyClientLike;
  amplitudeClient?: AmplitudeClientLike;
  environment?: IrisEnvironment;
  overridePhone?: string;
  overrideText?: string;
  theme?: "light" | "dark";
  className?: string;
  showIcon?: boolean;
  label?: string;
  // When provided, bypasses the internal URL resolution and uses this href directly
  overrideHref?: string;
  // Forces the normal iMessage CTA even when the waitlist flag is on. Used
  // when the CTA is rendered for a user already known to be off the waitlist
  // (e.g. inside the waitlist modal after an admitted user signs in), so it
  // never flips to "Join Waitlist" or re-opens the modal.
  disableWaitlist?: boolean;
};

export function IrisCtaLink({
  deviceId,
  ldClient,
  amplitudeClient: amplitudeClientProp,
  environment,
  overridePhone,
  overrideText,
  theme = "light",
  className,
  showIcon = true,
  label,
  overrideHref,
  disableWaitlist = false,
}: IrisCtaLinkProps) {
  const amplitudeFromContext = useAmplitude();
  const amplitudeClient = amplitudeClientProp ?? amplitudeFromContext;
  const [isWaitlistFlagEnabled, setIsWaitlistFlagEnabled] = useState(false);
  // A signed-in user who has left the waitlist (waitlist_status None or
  // "admitted") always sees the normal iMessage CTA, even when the flag is on.
  const [isOffWaitlist, setIsOffWaitlist] = useState(false);
  const { openWaitlistModal } = useWaitlistModal();

  const isWaitlistEnabled =
    !disableWaitlist && isWaitlistFlagEnabled && !isOffWaitlist;

  const resolvedLabel = isWaitlistEnabled
    ? "Join the waitlist"
    : (label ?? (isIOS() ? "iMessage Iris" : "Text Iris"));

  const [href, setHref] = useState(
    () =>
      overrideHref ??
      getInitialIMessageUrl({
        phoneNumber: overridePhone,
        message: overrideText,
      }),
  );

  useEffect(() => {
    // Skip URL resolution if overrideHref is provided
    if (overrideHref) {
      setHref(overrideHref);
      return;
    }

    let isMounted = true;

    resolveIMessageUrl({
      deviceId,
      ldClient,
      environment,
      phoneNumber: overridePhone,
      message: overrideText,
    }).then((resolvedHref) => {
      if (isMounted) {
        setHref(resolvedHref);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    deviceId,
    ldClient,
    environment,
    overridePhone,
    overrideText,
    overrideHref,
  ]);

  useEffect(() => {
    if (disableWaitlist) {
      return;
    }

    let isMounted = true;

    isIrisCtaWaitlistEnabled({
      deviceId,
      ldClient,
      environment,
    }).then((enabled) => {
      if (isMounted) {
        setIsWaitlistFlagEnabled(enabled);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [deviceId, ldClient, environment, disableWaitlist]);

  useEffect(() => {
    if (disableWaitlist) {
      return;
    }

    let isMounted = true;

    isCurrentUserOffWaitlist().then((offWaitlist) => {
      if (isMounted) {
        setIsOffWaitlist(offWaitlist);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [disableWaitlist]);

  const handleCtaClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    await trackIrisCtaClickWithOptions({
      source: "webflow",
      deviceId,
      theme,
      environment,
      amplitudeClient: amplitudeClient ?? undefined,
    });

    // Waitlist mode opens the in-app waitlist signup modal instead of
    // opening iMessage.
    if (isWaitlistEnabled) {
      openWaitlistModal();
      return;
    }

    // Use overrideHref directly if provided
    const resolvedHref =
      overrideHref ??
      (await resolveIMessageUrl({
        deviceId,
        ldClient,
        environment,
        phoneNumber: overridePhone,
        message: overrideText,
      }));
    setHref(resolvedHref);

    window.location.href = resolvedHref;
  };

  return (
    <PillLink
      id="iris-imessage-cta"
      href={isWaitlistEnabled ? "#" : href}
      onClick={handleCtaClick}
      theme={theme}
      className={`iris-cta-link ${isWaitlistEnabled ? "pill-primary" : ""} ${className}`
        .replace(/\s+/g, " ")
        .trim()}
      data-iris-imessage-cta=""
      data-waitlist-enabled={isWaitlistEnabled ? "true" : undefined}
    >
      {showIcon &&
        (isWaitlistEnabled ? (
          <LockClosedIcon className="iris-imessage-icon" aria-hidden="true" />
        ) : (
          <svg
            className="iris-imessage-icon"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 3C6.477 3 2 6.94 2 11.8c0 2.676 1.38 5.073 3.559 6.68L4.8 21l3.18-1.74c1.215.34 2.548.54 4.02.54 5.523 0 10-3.94 10-8.8S17.523 3 12 3Z"
              fill="#6E6E6E"
            />
          </svg>
        ))}
      <span className="iris-cta-label">{resolvedLabel}</span>
    </PillLink>
  );
}
